import React, { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Box, GlobalStyles, IconButton, Input, InputAdornment, Typography } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import InsertLinkOutlinedIcon from '@mui/icons-material/InsertLinkOutlined';

import ConfirmModal from '~/components/General/ConfirmModal';
import LimitReach from '~/components/General/LimitModal';
import NoRecords from '~/components/General/NoRecords';
import OlcButton from '~/components/General/OlcButton';
import CreateCustomFieldModal from '~/components/Settings/CustomFields/CreateModal';
import PurlCreateForm from './PurlCreateForm';
import PurlEditForm from './PurlEditForm';
import RecordsTable from './RecordsTable';

import {
  createQrCode,
  fetchQrCodes,
  updateQrCode,
  updateQrCodeStatus,
} from '~/redux/actions/qr-codes-actions';
import {
  fetchCustomFieldTypes,
  fetchCustomFieldsCatalog,
} from '~/redux/actions/customFields-actions';
import { failure, success } from '~/redux/actions/snackbar-actions';

import { DEFAULT_PAGINATION, ENGAGEMENT_TYPES } from '~/utils/constants';
import { MESSAGES } from '~/utils/message';

import {
  ContactSearchCIcon,
  GeneralInviteCIcon,
  ModalArchive,
  ModalRestore,
} from '~/assets/images';

import './styles.scss';

const Purls = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const purls = useSelector((state) => state.qrCodesReducers.qrCodes);
  const totalCount = useSelector((state) => state.qrCodesReducers.rowsCount);
  const totalRecordsInDB = useSelector((state) => state.qrCodesReducers.totalRecordsInDB);
  const loading = useSelector((state) => state.qrCodesReducers.loading);
  const perPage = useSelector(
    (state) => state.qrCodesReducers.perPage || DEFAULT_PAGINATION.pageSize
  );
  const lastPage = useSelector((state) => state.qrCodesReducers.lastPage);
  const from = useSelector((state) => state.qrCodesReducers.from);
  const to = useSelector((state) => state.qrCodesReducers.to);
  const currentPage = useSelector((state) => state.qrCodesReducers.currentPage);
  const customFields = useSelector((state) => state.customFieldsReducers.allCustomFields);

  const [modal, setModal] = useState({
    open: false,
    mode: 'create',
  });
  const [selectedPurl, setSelectedPurl] = useState(null);
  const [pagination, setPagination] = useState({ ...DEFAULT_PAGINATION, pageSize: 20 });
  const [initialLoad, setInitialLoad] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchApplied, setSearchApplied] = useState(false);
  const [customFieldCreateModal, setCustomFieldCreateModal] = useState(false);
  const [customFieldTypes, setCustomFieldTypes] = useState([]);
  const [pendingCustomFieldSelection, setPendingCustomFieldSelection] = useState(null);
  const [limitModal, setLimitModal] = useState(false);
  const [limitMessage, setLimitMessage] = useState('');
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    message: '',
    header: '',
    icon: '',
    loading: false,
    handleOk: () => {},
    handleClose: () => {},
    successButtonName: '',
  });

  const fetchPurls = async ({
    page = pagination.page,
    pageSize = pagination.pageSize,
    search = '',
  } = {}) =>
    dispatch(
      fetchQrCodes({
        pagination: { page, pageSize },
        search,
        engagementType: ENGAGEMENT_TYPES.PURL,
      })
    );

  const getCustomFieldDisplayName = (customField) =>
    customField?.value || customField?.title || customField?.label || customField?.name || '';

  const normalizeCustomFieldName = (value = '') =>
    String(value).trim().toLowerCase().replace(/\s+/g, '_');

  const extractCreatedCustomField = (response) => {
    const responseData = response?.data?.data;

    if (!responseData) {
      return null;
    }

    if (responseData.id) {
      return responseData;
    }

    if (responseData.customField?.id) {
      return responseData.customField;
    }

    if (responseData.field?.id) {
      return responseData.field;
    }

    return null;
  };

  const isSuccessfulPurlResponse = (response) =>
    response?.status === 200 || response?.status === 201;

  const getPurlErrorMessage = (error) =>
    error?.response?.data?.message ||
    error?.data?.message ||
    error?.message ||
    error?.response?.message ||
    MESSAGES.GENERAL_ERROR;

  const handleCustomFieldCreated = async (response, payload) => {
    const createdCustomField = extractCreatedCustomField(response);
    const customFieldName =
      getCustomFieldDisplayName(createdCustomField) || payload?.customFieldName;
    const catalogResponse = await dispatch(fetchCustomFieldsCatalog());
    const catalogFields = catalogResponse?.data?.data?.rows || [];

    const matchingCustomField =
      (createdCustomField?.id &&
        catalogFields.find((field) => String(field.id) === String(createdCustomField.id))) ||
      (customFieldName &&
        catalogFields.find(
          (field) =>
            normalizeCustomFieldName(getCustomFieldDisplayName(field)) ===
            normalizeCustomFieldName(customFieldName)
        )) ||
      createdCustomField ||
      (customFieldName ? { value: customFieldName } : null);

    if (matchingCustomField) {
      setPendingCustomFieldSelection(matchingCustomField);
    }
  };

  const handleCreatePurl = async (purlData) => {
    try {
      const response = await dispatch(createQrCode(purlData));
      if (isSuccessfulPurlResponse(response)) {
        setPagination((prev) => ({ ...prev, page: 1 }));
        await fetchPurls({ page: 1, pageSize: pagination.pageSize, search: '' });
        setSearchTerm('');
        setSearchApplied(false);
        dispatch(success(response?.data?.message));
      } else {
        dispatch(failure(getPurlErrorMessage(response)));
      }
      return response;
    } catch (error) {
      dispatch(failure(getPurlErrorMessage(error)));
      return error;
    }
  };

  const handleUpdatePurl = async (id, purlData) => {
    try {
      const response = await dispatch(updateQrCode(id, purlData));
      if (isSuccessfulPurlResponse(response)) {
        await fetchPurls({
          page: pagination.page,
          pageSize: pagination.pageSize,
          search: searchApplied ? searchTerm : '',
        });
        dispatch(success(response?.data?.message));
      } else {
        dispatch(failure(getPurlErrorMessage(response)));
      }
      return response;
    } catch (error) {
      dispatch(failure(getPurlErrorMessage(error)));
      return error;
    }
  };

  const handleArchivePurl = async (purlId) => {
    try {
      setConfirmModal((prev) => ({ ...prev, loading: true }));
      const response = await dispatch(updateQrCodeStatus(purlId, { status: 'Archived' }));
      if (isSuccessfulPurlResponse(response)) {
        await fetchPurls({
          page: pagination.page,
          pageSize: pagination.pageSize,
          search: searchApplied ? searchTerm : '',
        });
        setConfirmModal((prev) => ({ ...prev, open: false, loading: false }));
        dispatch(success(response?.data?.message));
      } else {
        setConfirmModal((prev) => ({ ...prev, loading: false }));
        dispatch(failure(getPurlErrorMessage(response)));
      }
      return response;
    } catch (error) {
      setConfirmModal((prev) => ({ ...prev, loading: false }));
      dispatch(failure(getPurlErrorMessage(error)));
      return error;
    }
  };

  const handleRestorePurl = async (purlId) => {
    try {
      setConfirmModal((prev) => ({ ...prev, loading: true }));
      const response = await dispatch(updateQrCodeStatus(purlId, { status: 'Active' }));
      if (isSuccessfulPurlResponse(response)) {
        await fetchPurls({
          page: pagination.page,
          pageSize: pagination.pageSize,
          search: searchApplied ? searchTerm : '',
        });
        setConfirmModal((prev) => ({ ...prev, open: false, loading: false }));
        dispatch(success(response?.data?.message));
      } else {
        setConfirmModal((prev) => ({ ...prev, loading: false }));
        dispatch(failure(getPurlErrorMessage(response)));
      }
      return response;
    } catch (error) {
      setConfirmModal((prev) => ({ ...prev, loading: false }));
      dispatch(failure(getPurlErrorMessage(error)));
      return error;
    }
  };

  useEffect(() => {
    let isMounted = true;

    const loadPurls = async () => {
      await fetchPurls({
        page: pagination.page,
        pageSize: pagination.pageSize,
        search: searchApplied ? searchTerm : '',
      });

      if (isMounted) {
        setInitialLoad(false);
      }
    };

    loadPurls();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, pagination.pageSize]);

  useEffect(() => {
    const loadCustomFieldTypes = async () => {
      const response = await dispatch(fetchCustomFieldTypes());
      if (response?.status === 200) {
        setCustomFieldTypes(response.data.data || []);
      }
    };

    loadCustomFieldTypes();
  }, [dispatch]);

  const handleSearch = () => {
    if (!searchTerm) return;

    setPagination((prev) => ({ ...prev, page: 1 }));
    fetchPurls({
      page: 1,
      pageSize: pagination.pageSize,
      search: searchTerm,
    });
    setSearchApplied(true);
  };

  const handleSearchChange = (event) => {
    const nextSearchTerm = event.target.value.trimStart();
    setSearchTerm(nextSearchTerm);
    if (!nextSearchTerm) {
      setSearchApplied(false);
      setPagination((prev) => ({ ...prev, page: 1 }));
      fetchPurls({ page: 1, pageSize: pagination.pageSize, search: '' });
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (event.target.value.trimStart()) {
        handleSearch();
      }
    }
  };

  const handleViewHistory = (purl) => {
    navigate(`/engagement/purl-history/${purl.id}`, { state: { purl } });
  };

  const handleClosePurlModal = () => {
    setModal((prev) => ({ ...prev, open: false }));
    setSelectedPurl(null);
  };

  return (
    <>
      <GlobalStyles styles={{ body: { backgroundColor: '#f9fafb' } }} />
      <Box className="olcWrapper olcSettingsListPage qrCodesPageWrapper purlsPageWrapper">
        <Box className="olcScreenCard olcSettingsListCard qrCodesScreenCard">
          <Box className="olcHeaderWrap">
            <Box>
              <Typography className="pageTitle">{MESSAGES.SETTINGS.PURLS.TITLE}</Typography>
            </Box>
            <Box className="pageNewBtn">
              <OlcButton
                variant="primary"
                onClick={() => setModal({ open: true, mode: 'create' })}
                startIcon={<img src={GeneralInviteCIcon} alt="actions" />}
              >
                <Typography>{MESSAGES.SETTINGS.PURLS.ADD_NEW}</Typography>
              </OlcButton>
            </Box>
          </Box>
          <Box className="olcTableToolbar qrCodesToolbar">
            <Box className="searchWrapper olcSearchWrapper olcSearchWrapperQR">
              <Input
                className={`searchInput ${searchApplied ? 'searchApplied' : ''}`}
                type="text"
                value={searchTerm}
                onChange={handleSearchChange}
                onKeyDown={handleKeyDown}
                placeholder="Search by PURL name"
                title="Search by PURL name"
                endAdornment={
                  <InputAdornment position="end" className="searchInputAdornment">
                    <IconButton
                      aria-label="search"
                      edge="end"
                      className="searchIcon"
                      onClick={handleSearch}
                    >
                      <img src={ContactSearchCIcon} alt="search" />
                    </IconButton>
                    {searchTerm?.trim() && (
                      <IconButton
                        className="cpClearInputBtn"
                        size="small"
                        onClick={() => {
                          setSearchTerm('');
                          setSearchApplied(false);
                          setPagination((prev) => ({ ...prev, page: 1 }));
                          fetchPurls({ page: 1, pageSize: pagination.pageSize, search: '' });
                        }}
                        edge="end"
                        sx={{ padding: '4px' }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    )}
                  </InputAdornment>
                }
              />
            </Box>
          </Box>
          {!initialLoad && !loading && !searchApplied && (totalCount || 0) <= 0 ? (
            <Box>
              <NoRecords
                icon={
                  <div
                    style={{
                      height: '40px',
                      width: '40px',
                      justifyContent: 'center',
                      alignItems: 'center',
                      display: 'flex',
                    }}
                  >
                    <InsertLinkOutlinedIcon
                      sx={{ height: '40px', width: '40px', color: '#ed5c2f' }}
                    />
                  </div>
                }
                title="No PURLs Active"
                message={
                  <Box className="noRecordsMessageQR">
                    PURLs let you track personalized engagement from your mailers. Each contact can
                    receive a dynamic URL so you know who visited and when.
                  </Box>
                }
                messageWidth="min(100%, 720px)"
                minBoxHeight="220px"
                boxPadding="24px 40px"
              />
            </Box>
          ) : (
            <RecordsTable
              purls={purls}
              setSelectedPurl={setSelectedPurl}
              setEditModal={() => {
                setModal({ open: true, mode: 'edit' });
              }}
              updatePagination={setPagination}
              pagination={pagination}
              totalCount={totalCount}
              totalRecordsInDB={totalRecordsInDB}
              lastPage={lastPage}
              from={from}
              to={to}
              currentPage={currentPage}
              perPage={perPage}
              initialLoad={initialLoad}
              isLoading={loading}
              onArchive={(purl) => {
                setConfirmModal({
                  open: true,
                  message: (
                    <Box style={{ marginBottom: '4px' }}>
                      Are you sure you want to archive this PURL? Archiving will keep it linked to
                      all existing orders and other work, but it will no longer be active for new
                      activity.
                    </Box>
                  ),
                  header: 'Archive PURL',
                  icon: ModalArchive,
                  loading: false,
                  handleOk: () => handleArchivePurl(purl.id),
                  handleClose: () => setConfirmModal((prev) => ({ ...prev, open: false })),
                  successButtonName: 'Yes',
                });
              }}
              onRestore={(purl) => {
                setConfirmModal({
                  open: true,
                  message: (
                    <Box style={{ marginBottom: '4px' }}>
                      Restoring this PURL will make it available again for templates.
                    </Box>
                  ),
                  header: 'Restore',
                  icon: ModalRestore,
                  loading: false,
                  cancelButtonName: 'Cancel',
                  handleOk: () => handleRestorePurl(purl.id),
                  handleClose: () => setConfirmModal((prev) => ({ ...prev, open: false })),
                  successButtonName: 'Restore',
                });
              }}
              onViewHistory={handleViewHistory}
            />
          )}
        </Box>
      </Box>

      {modal.mode === 'edit' ? (
        <PurlEditForm
          openModal={modal.open}
          handleCloseModal={handleClosePurlModal}
          selectedPurl={selectedPurl}
          onUpdatePurl={handleUpdatePurl}
          onCreatePurl={handleCreatePurl}
          customFields={customFields}
          onCreateCustomFieldClick={() => setCustomFieldCreateModal(true)}
          pendingCustomFieldSelection={pendingCustomFieldSelection}
          onPendingCustomFieldSelectionConsumed={() => setPendingCustomFieldSelection(null)}
        />
      ) : (
        <PurlCreateForm
          openModal={modal.open}
          handleCloseModal={handleClosePurlModal}
          onCreatePurl={handleCreatePurl}
          onUpdatePurl={handleUpdatePurl}
          customFields={customFields}
          onCreateCustomFieldClick={() => setCustomFieldCreateModal(true)}
          pendingCustomFieldSelection={pendingCustomFieldSelection}
          onPendingCustomFieldSelectionConsumed={() => setPendingCustomFieldSelection(null)}
        />
      )}

      <CreateCustomFieldModal
        openModal={customFieldCreateModal}
        handleCloseModal={() => setCustomFieldCreateModal(false)}
        pagination={{ ...DEFAULT_PAGINATION, pageSize: 20 }}
        updatePagination={() => {}}
        customFieldTypes={customFieldTypes}
        defaultType="url"
        disableType
        onSuccess={handleCustomFieldCreated}
        onLimitReached={(message) => {
          setCustomFieldCreateModal(false);
          setLimitMessage(message);
          setLimitModal(true);
        }}
      />
      <LimitReach
        open={limitModal}
        onClose={() => {
          setLimitModal(false);
          setTimeout(() => setLimitMessage(''), 100);
        }}
        message={limitMessage}
        resourceType="custom fields"
      />

      <ConfirmModal openConfirmModal={confirmModal} />
    </>
  );
};

export default Purls;
